import type { DifficultyId } from "../games/futatsu-de-pittari/manifest";

export const STORAGE_KEY = "puzzle-collection.futatsu-de-pittari.v1";

export interface BestRecord {
  readonly mistakeCount: number;
  readonly elapsedMs: number;
}

export interface SaveData {
  readonly soundEnabled: boolean;
  readonly bestRecords: Partial<Record<DifficultyId, BestRecord>>;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const DEFAULT_DATA: SaveData = Object.freeze({
  soundEnabled: true,
  bestRecords: Object.freeze({}),
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseBestRecord(value: unknown): BestRecord | null {
  if (!isRecord(value)) return null;
  const { mistakeCount, elapsedMs } = value;
  if (
    !Number.isInteger(mistakeCount) ||
    (mistakeCount as number) < 0 ||
    typeof elapsedMs !== "number" ||
    !Number.isFinite(elapsedMs) ||
    elapsedMs < 0
  ) {
    return null;
  }
  return { mistakeCount: mistakeCount as number, elapsedMs };
}

function parseSaveData(value: unknown): SaveData {
  if (!isRecord(value)) return DEFAULT_DATA;
  const recordsSource = isRecord(value.bestRecords) ? value.bestRecords : {};
  const bestRecords: Partial<Record<DifficultyId, BestRecord>> = {};

  for (const difficultyId of ["easy", "normal", "hard"] as const) {
    const record = parseBestRecord(recordsSource[difficultyId]);
    if (record) bestRecords[difficultyId] = record;
  }

  return {
    soundEnabled: typeof value.soundEnabled === "boolean" ? value.soundEnabled : true,
    bestRecords,
  };
}

export function isBetterRecord(candidate: BestRecord, current?: BestRecord): boolean {
  if (!current) return true;
  if (candidate.mistakeCount !== current.mistakeCount) {
    return candidate.mistakeCount < current.mistakeCount;
  }
  return candidate.elapsedMs < current.elapsedMs;
}

export class GameStorage {
  private data: SaveData;

  public constructor(private readonly storage?: StorageLike) {
    this.data = this.read();
  }

  public getSnapshot(): SaveData {
    return {
      soundEnabled: this.data.soundEnabled,
      bestRecords: { ...this.data.bestRecords },
    };
  }

  public setSoundEnabled(enabled: boolean): void {
    this.data = { ...this.data, soundEnabled: enabled };
    this.write();
  }

  public updateBest(difficultyId: DifficultyId, candidate: BestRecord): boolean {
    const current = this.data.bestRecords[difficultyId];
    if (!isBetterRecord(candidate, current)) return false;

    this.data = {
      ...this.data,
      bestRecords: { ...this.data.bestRecords, [difficultyId]: candidate },
    };
    this.write();
    return true;
  }

  public resetRecords(): void {
    this.data = { ...this.data, bestRecords: {} };
    this.write();
  }

  private read(): SaveData {
    if (!this.storage) return DEFAULT_DATA;
    try {
      const stored = this.storage.getItem(STORAGE_KEY);
      return stored === null ? DEFAULT_DATA : parseSaveData(JSON.parse(stored));
    } catch {
      return DEFAULT_DATA;
    }
  }

  private write(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // 保存できない環境でも、現在のセッションではそのまま遊べる。
    }
  }
}
